import { captureException } from "@sentry/browser";
import { useLocation } from "@tanstack/react-router";
import type {
  ClientOrderByArgs,
  ClientPaginationState,
  ClientPaginatedResponse,
} from "api-types/common/commonResponses";
import type { ClientUserHydrated, ClientUserHydratedWithRoles } from "api-types/users/usersResponses";
import { FetchError } from "common/types/fetchError";
import { containsFalsy } from "common/utils/arrays";
import { fetchWithTimeout } from "common/utils/fetchHelpers";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import type { SWRConfiguration, SWRResponse } from "swr";
import useSWR from "swr";
import useDebounce from "ui/hooks/useDebounce";
import { VALIDATION_ENDPOINT } from "./auth";
import { loginPath } from "./routeHelpers";
import { getFetchHeaders } from "./utils/fetchAuthHelpers";

const userFetchOptions = {
  shouldRetryOnError: false,
};

function useFetchErrorPath(hasError: boolean): string | undefined {
  // Healthy fetch consumers do not need to re-render when shareable URL metadata changes.
  return useLocation({
    select: (location) => {
      if (!hasError) {
        return undefined;
      }
      return location.pathname + location.searchStr + (location.hash ? "#" + location.hash : "");
    },
  });
}

export interface UseFetchResponse<T> extends SWRResponse<T, FetchError> {
  user?: ClientUserHydrated;
  fetchKey: string;
  loading: boolean;
}

interface FetchOptions<T, E> extends SWRConfiguration<T, E> {
  handleErrorLocally?: boolean;
}

/**
 * React hook that functions as a wrapper around useSWR. Function
 * is generic and must be provided a type T that represents the
 * expected type of the response data.
 *
 * See docs: https://swr.vercel.app
 *
 * @param {string} fetchKey: Unique identifier (URL path) for the data being fetched
 * @param {any[]} dependsOn: Optional array of values that must all be truth-y for the
 *                               HTTP request to be made
 * @param {SWRConfiguration<T, FetchError>} options: SWR configuration options
 *
 * @returns UseFetchResponse<T>
 */
export function useFetch<T>(
  fetchKey: string,
  dependsOn: any[] = [],
  options?: FetchOptions<T, FetchError>,
  body?: Object,
): UseFetchResponse<T> {
  const authResp = useSWR<ClientUserHydrated, FetchError>(
    createFetchKey(VALIDATION_ENDPOINT, dependsOn),
    fetcher,
    userFetchOptions,
  );

  const handleSwallowError = async (fetchError: FetchError) => {
    let message = `Fetch error: ${fetchError.status}`;
    if (fetchError.message) {
      message += ` (${fetchError.message})`;
    }
    console.error(message);

    if ([401, 404, 409].includes(fetchError.status ?? Number.POSITIVE_INFINITY)) {
      return;
    }

    captureException(new Error(message), {
      extra: {
        status: fetchError.status,
        message: fetchError.message,
        fetchKey,
      },
    });
  };

  const { handleErrorLocally, ...fetchOptions } = options || {};

  const fetchKeyWithBody = body
    ? [createFetchKey(fetchKey, dependsOn), JSON.stringify(body)]
    : createFetchKey(fetchKey, dependsOn);

  const dataResp = useSWR<T, FetchError>(
    authResp.data ? fetchKeyWithBody : null,
    () => fetcher(fetchKey, true, {}, body),
    {
      ...(handleErrorLocally ? { onError: handleSwallowError } : undefined),
      ...fetchOptions,
    },
  );

  const loading = !dataResp.data && !dataResp.error;
  const currentPath = useFetchErrorPath(!!authResp.error || !!dataResp.error);

  if (authResp.error) {
    void handleSWRFetchError(authResp, currentPath);

    return { ...dataResp, fetchKey, user: authResp.data, loading };
  }

  if (dataResp.error) {
    void handleSWRFetchError(dataResp, currentPath);
  }

  return { ...dataResp, fetchKey, user: authResp.data, loading };
}

/**
 * Constructs a fetch key with pagination, search, and orderBy parameters.
 */
function getPaginatedFetchKey(
  fetchKey: string,
  pagination: ClientPaginationState,
  paginationMethod: "offset" | "cursor",
  search?: string,
  orderBy?: ClientOrderByArgs<any>,
) {
  const fetchKeyArr = fetchKey.split("?");
  const fetchKeyBasePath = fetchKey.includes("?") ? fetchKeyArr[0] : fetchKey;
  const fetchKeyExistingParams = fetchKey.includes("?") ? `&${fetchKeyArr[1]}` : "";

  let fullFetchKey = `${fetchKeyBasePath}?limit=${pagination.pageSize}${fetchKeyExistingParams}`;

  switch (paginationMethod) {
    case "offset": {
      fullFetchKey = fullFetchKey.concat(`&offset=${pagination.pageIndex * pagination.pageSize}`);
      break;
    }
    case "cursor": {
      // TODO: For now, only handling 'after' cursors for pagination and relying on pageIndex to go back
      const prevPageIndex = pagination.pageIndex - 1;
      const cursor = prevPageIndex >= 0 ? pagination.pageInfoByIndex?.[prevPageIndex]?.endCursor : undefined;

      fullFetchKey = cursor ? fullFetchKey.concat(`&after=${cursor}`) : fullFetchKey;
      break;
    }
  }

  if (search) {
    fullFetchKey = fullFetchKey.concat(`&search=${search}`);
  }

  if (orderBy) {
    fullFetchKey = fullFetchKey.concat(`&orderBy=${Object.keys(orderBy)[0]}&order=${Object.values(orderBy)[0]}`);
  }

  return fullFetchKey;
}

export interface UsePaginatedFetchResponse<T> extends UseFetchResponse<ClientPaginatedResponse<T>> {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  orderBy?: ClientOrderByArgs<T>;
  setOrderBy: Dispatch<SetStateAction<ClientOrderByArgs<T> | undefined>>;
  pagination: ClientPaginationState;
  setPagination: Dispatch<SetStateAction<ClientPaginationState>>;
}

/**
 * React hook that functions as a wrapper around useSWR. Functions the same as useFetch,
 * but with additional state and methods for managing pagination and search.
 *
 * @param fetchKey:  Unique identifier (URL path) for the data being fetched
 * @param dependsOn: Optional array of values that must all be truth-y for the HTTP request to be made
 * @param options:   SWR configuration options, with additional pagination-specific options
 */
export function usePaginatedFetch<T>(
  fetchKey: string,
  dependsOn: any[] = [],
  options?: FetchOptions<ClientPaginatedResponse<T>, FetchError> & {
    pageSize?: number;
    paginationMethod?: "offset" | "cursor";
    debounceTimeout?: number;
  },
): UsePaginatedFetchResponse<T> {
  const { pageSize, debounceTimeout, paginationMethod = "offset", ...fetchOptions } = options || {};
  const [search, setSearch] = useState("");
  const [orderBy, setOrderBy] = useState<ClientOrderByArgs<T>>();
  const debouncedSearch = useDebounce(search, debounceTimeout || 500);
  const [pagination, setPagination] = useState<ClientPaginationState>({
    pageSize: pageSize || 50,
    pageIndex: 0,
    pageInfoByIndex: {},
  });
  const fetchKeyParsed = getPaginatedFetchKey(fetchKey, pagination, paginationMethod, debouncedSearch, orderBy);
  const useFetchResponse = useFetch<ClientPaginatedResponse<T>>(fetchKeyParsed, dependsOn, {
    ...fetchOptions,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (useFetchResponse.data && paginationMethod === "cursor") {
      setPagination((prev) => ({
        ...prev,
        pageInfoByIndex: {
          ...prev.pageInfoByIndex,
          [pagination.pageIndex]: {
            hasNextPage: useFetchResponse?.data?.hasNextPage || false,
            hasPreviousPage: useFetchResponse?.data?.hasPreviousPage || false,
            startCursor: useFetchResponse?.data?.startCursor || null,
            endCursor: useFetchResponse?.data?.endCursor || null,
          },
        },
      }));
    }
  }, [useFetchResponse.data, paginationMethod, pagination.pageIndex]);

  // need to reset the page index when searching
  useEffect(
    () =>
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        pageInfoByIndex: {},
      })),
    [search],
  );

  // Reset pagination when the base fetchKey changes (e.g., filters change)
  useEffect(
    () =>
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
        pageInfoByIndex: {},
      })),
    [fetchKey],
  );

  return {
    ...useFetchResponse,
    pagination,
    setPagination,
    search,
    setSearch,
    orderBy,
    setOrderBy,
  };
}

/**
 * Wrapper around useFetch that does not automatically revalidate
 * the response data. See https://swr.vercel.app/docs/revalidation#disable-automatic-revalidations.
 *
 * Arguments are the same as useFetch.
 */
export function useFetchImmutable<T>(
  fetchKey: string,
  dependsOn: any[] = [],
  options?: SWRConfiguration<T, FetchError>,
  body?: Object,
): UseFetchResponse<T> {
  return useFetch<T>(
    fetchKey,
    dependsOn,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      ...options,
    },
    body,
  );
}

export interface UseFetchNoAuthResponse<T> {
  data?: T;
  fetchKey: string;
  loading: boolean;
  error?: FetchError;
}

/**
 * Wrapper around useFetch for unauthenticated endpoints.
 *
 * Arguments are the same as useFetch.
 */
export function useFetchNoAuth<T>(
  fetchKey: string,
  dependsOn: any[] = [],
  options?: SWRConfiguration<T, FetchError>,
): UseFetchNoAuthResponse<T> {
  const key = createFetchKey(fetchKey, dependsOn);
  const res = useSWR<T, FetchError>(key, fetcherNoAuth, options);
  const loading = key !== null && !res.data && !res.error;
  const currentPath = useFetchErrorPath(!!res.error);

  if (res.error) {
    void handleSWRFetchError(res, currentPath);
  }

  return { fetchKey, data: res.data, loading, error: res.error };
}

/**
 * Wrapper around useFetch for calls that should fail quietly - no redirects or error handling.
 *
 * Arguments are the same as useFetch.
 */
export function useFetchNoHandle<T>(
  fetchKey: string,
  dependsOn: any[] = [],
  options?: SWRConfiguration<T, FetchError>,
): UseFetchNoAuthResponse<T> {
  const key = createFetchKey(fetchKey, dependsOn);
  const res = useSWR<T, FetchError>(key, fetcher, options);
  const loading = key !== null && !res.data && !res.error;

  return { fetchKey, data: res.data, loading, error: res.error };
}

interface GetUserResponse {
  user: ClientUserHydratedWithRoles | null;
  loading: boolean;
  error: any;
}

export function useUser(redirect?: boolean): GetUserResponse {
  const res = useSWR<ClientUserHydratedWithRoles>(VALIDATION_ENDPOINT, fetcher, {
    ...userFetchOptions,
    dedupingInterval: 0,
  });
  const currentPath = useFetchErrorPath(!!res.error && !!redirect);

  if (res.error && redirect) {
    void handleSWRFetchError(res, currentPath);
  }

  return {
    user: res.data ?? null,
    loading: !res.error && !res.data,
    error: res.error,
  };
}

async function handleSWRFetchError<T>(res: SWRResponse<T, FetchError>, currentUrl?: string) {
  if (res.error instanceof FetchError) {
    handleFetchError(res.error, currentUrl);
  }
}

export function handleFetchError(error: FetchError, currentUrl?: string) {
  if (error instanceof FetchError) {
    const code = error.status;
    switch (code) {
      case 401:
        window.location.href = loginPath(currentUrl);
        break;
    }
  }
}

export async function fetcher<T>(
  key: string,
  useAuth = true,
  customHeaders = {},
  body?: Object,
  timeout?: number,
): Promise<T> {
  const options: RequestInit = {
    headers: await getFetchHeaders(useAuth, customHeaders),
  };

  if (body) {
    options.method = "POST";
    options.body = JSON.stringify(body);
  }

  const res = timeout ? await fetchWithTimeout(key, options, timeout) : await fetch(key, options);
  if (!res.ok) {
    throw new FetchError(res.status, res.statusText);
  }

  return await res.json().catch((_) => {
    throw new FetchError(res.status, "Could not parse JSON response");
  });
}

export async function fetcherNoAuth<T>(key: string, customHeaders = {}, body?: Object): Promise<T> {
  return fetcher(key, false, customHeaders, body);
}

export function createFetchKey(fetchKey: string, dependsOn: any[] = []) {
  // Sometimes the fetch key is built using interpolation of an undefined
  // variable, so `/some/path/${id}` actually becomes `/some/path/undefined`,
  // which is quite invalid, so let's avoid fetching these endpoints
  return !containsFalsy(dependsOn) && !!fetchKey && !fetchKey.includes("/undefined") ? fetchKey : null;
}
